import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';

type Props = {
  open: boolean;
  isTrial: boolean;
  nomeImobiliaria: string;
  siteAtual: string;
  onNomeChange: (value: string) => void;
  onSiteChange: (value: string) => void;
  nomeError?: string;
  onContinue: () => void;
};

export function ImobiliariaOnboardingDialog({
  open,
  isTrial,
  nomeImobiliaria,
  siteAtual,
  onNomeChange,
  onSiteChange,
  nomeError,
  onContinue,
}: Props) {
  const nomeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      nomeRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nomeRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Sobre a sua imobiliária</DialogTitle>
          <DialogDescription>
            {isTrial
              ? 'Para iniciar o teste, precisamos do nome da imobiliária. O site é opcional.'
              : 'Informe o nome da imobiliária para continuar. O site é opcional.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="onboarding-nome-imobiliaria" className="text-slate-700">
              Nome da imobiliária *
            </Label>
            <Input
              ref={nomeRef}
              id="onboarding-nome-imobiliaria"
              value={nomeImobiliaria}
              onChange={(e) => onNomeChange(e.target.value)}
              placeholder="Nome da sua imobiliária"
              className={cn(
                'h-12 border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 bg-white/80',
                nomeError && 'border-red-500',
              )}
              autoComplete="organization"
            />
            {nomeError ? <p className="text-xs text-red-600">{nomeError}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="onboarding-site-atual" className="text-slate-700">
              Site atual (opcional)
            </Label>
            <Input
              id="onboarding-site-atual"
              value={siteAtual}
              onChange={(e) => onSiteChange(e.target.value)}
              placeholder="https://www.suaimobiliaria.com.br"
              className="h-12 border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 bg-white/80"
              autoComplete="url"
            />
          </div>
        </div>
        <DialogFooter className="sm:justify-end">
          <Button type="button" className="w-full bg-indigo-600 hover:bg-indigo-700 sm:w-auto" onClick={onContinue}>
            Continuar
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
